import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-05-27 14:18:05 +0200 (Wed, 27 May 2026) $
//  Author    : Albert Charlier, Dirk Rothe, Tobias Schwarzkopf
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163319 $
//  Purpose   : facets
// =============================================================================
// --- Third-party imports
import React from 'react';
import { Label, Menu, Accordion, Form, Segment, Button as SuiButton } from 'semantic-ui-react';
import clsx from 'clsx';
// --- Framework imports
import { get, filterItems, dict, mapItems, size } from 'ldash';
import log from 'uix/logging';
import { translateLangCode } from 'uix/defs';
import { Actions, Sel } from 'uix/registries';
import { useState, useSelector, useActions, usePersistent, useCallback } from 'uix/hooks';
import { XPopup, XMenu, Link, DebugFeature } from 'uix/components';
// --- Local imports
import styles from './facets.css';
import { Button, Form as NativeForm } from './nativeComps';
// --- Globals
const EMPTY_TOKEN = 'zzz';
const useFacetNavigation = ({ paramName = 'facets' } = {})=>{
    let go = useActions('go');
    let toggleFacetValue = (checked, value, incquery, decquery)=>{
        let queryFacets = checked ? incquery : decquery;
        let paramValue = null;
        if (queryFacets) {
            paramValue = queryFacets.replace('$(facetval)', value);
        }
        go({
            updateQuery: {
                [paramName]: paramValue,
                offset: null
            }
        });
    };
    return toggleFacetValue;
};
export const FacetAlphaIndex = ({ type = 'title', facets, className })=>{
    let toggleFacetValue = useFacetNavigation();
    let fname = `alpha.${type}1`;
    let data = facets?.[fname];
    if (!data && facets) return null //`no data for ${fname}`
    ;
    let alphabet = 'abcdefghijklmnopqrstuvwxyz'.split('');
    let byChar = dict(data.items.map(({ value, ...rest })=>[
            value,
            rest
        ]));
    let { incquery, decquery } = data;
    let isSelected = (c)=>byChar[c]?.selected;
    let isAvailable = (c)=>byChar[c]?.count > 0;
    return /*#__PURE__*/ _jsxs("div", {
        className: clsx(className, styles.facetAlpha),
        children: [
            /*#__PURE__*/ _jsx(Button, {
                className: styles.navigator,
                content: "<"
            }),
            /*#__PURE__*/ _jsx("div", {
                className: styles.alpha,
                children: alphabet.map((c)=>/*#__PURE__*/ _jsx(Button, {
                        disabled: !isAvailable(c),
                        active: isSelected(c),
                        className: clsx(styles.item, isAvailable(c) && styles.available, isSelected(c) && styles.active),
                        onClick: ()=>isAvailable(c) ? toggleFacetValue(!isSelected(c), c, incquery, decquery) : null,
                        children: c
                    }, c))
            }),
            /*#__PURE__*/ _jsx(Button, {
                className: styles.navigator,
                content: ">"
            })
        ]
    });
};
export const Facets = ({ hideEmpty = true, facets, mode, className, paramName, compact = false, bare = false })=>{
    let i18n = useSelector(Sel.i18n);
    let language = i18n.language;
    let request = useSelector(Sel.request);
    let itypes = useSelector(Sel.itypes);
    let domains = useSelector(Sel.domains);
    let toggleFacetValue = useFacetNavigation({
        paramName
    });
    let [active, setActive] = useState({}) // is facet box open (accordion)
    ;
    let [showAll, setShowAll] = useState({});
    let toggleBox = (facetName, wasActive)=>{
        setActive({
            ...active,
            [facetName]: !wasActive
        });
        if (wasActive && showAll[facetName]) {
            setShowAll({
                ...showAll,
                [facetName]: false
            });
        }
    };
    let _toggleFacetValue = (facetName, isChecked, valueName, incquery, decquery)=>{
        toggleBox(facetName, false); // ensure box is opened if individual values are switched
        toggleFacetValue(isChecked, valueName, incquery, decquery);
    };
    let translateLabel = (name, value, caption)=>{
        if (caption) {
            return caption;
        } else if (value == 'zzz') {
            return i18n['empty'];
        } else {
            switch(name){
                case 'collections':
                    return value;
                case 'pagemode':
                    return i18n[`pagemodeFacet_${value}`];
                case 'language':
                    return translateLangCode(value, language);
                case 'contentSource':
                    return i18n[`ISIL-${value}`];
                case 'odid':
                    return get(domains.data, `${domains.odid2name[value]}.description`, value);
                case 'domainGroup':
                    return get(domains.data, `${value}.description`, value);
                case 'licence':
                    return value;
                case 'released':
                    return i18n[`ot_state_${value}`];
                case 'state.metadata':
                    return i18n[`metadata_state_${value}`];
                case 'state.qs':
                    return i18n[`ot_qs_state_${value}`];
                case 'ftmode':
                    return value == 'empty' ? i18n['ftmodeWithoutFulltext'] : i18n['ftmodeWithFulltext'];
                case 'subtype':
                    return i18n[`types_metadata_${itypes.byId[1][value]}`];
                case 'name.subtype':
                    return i18n[`types_name_${itypes.byId[13][value]}`];
                case 'subject.subtype':
                    return i18n[`types_subject_${itypes.byId[19][value]}`];
                case 'name.subjects':
                    return i18n[`gnd_subj_${value}`];
                case 'name.countries':
                    return i18n[`gnd_reg_${value}`];
                case 'subject.gnd.sc1':
                    return i18n[`gnd_subj_${value}`];
                case 'subject.gnd.sc2':
                    return i18n[`gnd_subj_${value}`];
                case 'type':
                    return i18n[`types_metadata_${value}`];
                default:
                    if ('d100 dr100 d50 dr50 d10 dr10'.indexOf(name) > -1) {
                        let period = parseInt(name.replace(/\D/g, ''));
                        if (value == EMPTY_TOKEN) {
                            return i18n['bracketUnknown'];
                        }
                        let year = parseInt(value);
                        // fetch from dconfig
                        let dateShift = 1;
                        return `${parseInt(year)}-${year + period - 1 + dateShift}`;
                    }
                    return value;
            }
        }
    };
    let isSelected = (item)=>item.selected;
    let filledFacets = filterItems(facets, (name, data)=>data.items && (data.items.length > 1 || data.items.some(isSelected)));
    filledFacets = filledFacets.filter(([name])=>!name.startsWith('alpha.'));
    if (filledFacets.length == 0) {
        return null;
    }
    let get_is_empty_hidden = (name)=>{
        return hideEmpty;
    };
    const SHOW_MANY = 100;
    const SHOW_LESS = 6;
    const SHOW_ALL_FACET_NAMES = [
        'd10',
        'd100'
    ];
    let get_limit = (name)=>{
        if (SHOW_ALL_FACET_NAMES.includes(name)) {
            return SHOW_MANY * 10;
        }
        if (showAll[name]) {
            return SHOW_MANY;
        }
        return SHOW_LESS;
    };
    let boxes = filledFacets.map(([name, data], i)=>{
        // do not show d10 facet, when d100 exists and is not selected
        if (name === 'd10' && facets.d100 && !get(facets, 'd100.items', []).some(isSelected)) {
            return null;
        }
        let item = ({ value, count, caption, selected, decquery })=>{
            let label_count = {};
            if (mode === 'sui') {
                label_count = /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            style: {
                                float: 'right',
                                paddingRight: '3px',
                                fontSize: '0.9em'
                            },
                            children: count
                        }),
                        /*#__PURE__*/ _jsx("label", {
                            style: {
                                display: 'flex',
                                flex: 1
                            },
                            children: translateLabel(name, value, caption)
                        })
                    ]
                });
            } else {
                label_count = /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs("span", {
                            children: [
                                translateLabel(name, value, caption),
                                " "
                            ]
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "count",
                            style: {
                                marginLeft: 'auto',
                                float: 'right',
                                paddingRight: '3px',
                                fontSize: '0.9em'
                            },
                            children: count
                        })
                    ]
                });
            }
            if (mode === 'sui') {
                return /*#__PURE__*/ _jsx(Form.Checkbox, {
                    label: label_count,
                    name: `${name}_${value}`,
                    checked: selected,
                    style: {
                        width: '100%'
                    },
                    onChange: (e, r)=>_toggleFacetValue(name, r.checked, value, data.incquery, decquery)
                }, value);
            } else {
                return /*#__PURE__*/ _jsx(NativeForm.Checkbox, {
                    className: styles.checkbox,
                    label: label_count,
                    checked: selected,
                    onChange: (e)=>_toggleFacetValue(name, e.target.checked, value, data.incquery, decquery)
                }, value);
            }
        };
        let filter_items = (e)=>e.value // has a value
        ;
        if (get_is_empty_hidden(name)) {
            filter_items = (e)=>e.value && e.value != 'zzz';
        }
        const items = data.items.filter(filter_items).slice(0, get_limit(name)).map((e)=>item(e));
        let label = i18n[`index_${name}s`];
        let group = {};
        if (mode === 'sui') {
            group = /*#__PURE__*/ _jsx(Form, {
                children: /*#__PURE__*/ _jsx(Form.Group, {
                    grouped: true,
                    children: /*#__PURE__*/ _jsx("div", {
                        className: styles.itemBox,
                        children: items
                    })
                })
            });
        } else {
            group = /*#__PURE__*/ _jsx(NativeForm.Group, {
                title: label || name,
                children: items
            }, i);
        }
        let someSelected = data.items.some(isSelected);
        let isActive = active[name] !== undefined ? active[name] : someSelected;
        if (mode === 'sui') {
            let clickShowAll = (e, name)=>{
                // do not propagate click event
                e.stopPropagation();
                setShowAll({
                    ...showAll,
                    [name]: !showAll[name]
                });
            };
            let boxHeader = /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    label || name,
                    " "
                ]
            });
            return /*#__PURE__*/ _jsxs(Menu.Item, {
                children: [
                    /*#__PURE__*/ _jsx(Accordion.Title, {
                        active: isActive,
                        content: boxHeader,
                        index: i,
                        onClick: ()=>toggleBox(name, isActive)
                    }),
                    /*#__PURE__*/ _jsxs(Accordion.Content, {
                        active: isActive,
                        children: [
                            group,
                            isActive && !SHOW_ALL_FACET_NAMES.includes(name) && !someSelected && !showAll[name] && /*#__PURE__*/ _jsx(SuiButton, {
                                secondary: true,
                                compact: true,
                                size: "mini",
                                icon: {
                                    name: !showAll[name] ? 'plus' : 'minus',
                                    color: 'black'
                                },
                                style: {
                                    position: 'absolute',
                                    left: '40%',
                                    bottom: '0px',
                                    lineHeight: '0.8em'
                                },
                                content: "mehr",
                                onClick: (e)=>clickShowAll(e, name),
                                title: !showAll[name] ? `Show ${Math.min(size(data.items), SHOW_MANY)} of ${size(data.items)}` : ''
                            })
                        ]
                    })
                ]
            }, name);
        } else {
            return group;
        }
    });
    if (mode === 'sui') {
        let content = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Accordion, {
                    as: Menu,
                    vertical: true,
                    attached: bare ? true : undefined,
                    children: boxes
                }),
                /*#__PURE__*/ _jsx(DebugFeature, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        style: {
                            fontSize: '0.7em',
                            marginTop: '-10px'
                        },
                        children: [
                            "total: ",
                            facets.numhits
                        ]
                    })
                })
            ]
        });
        if (bare) {
            return content;
        }
        return /*#__PURE__*/ _jsx(Segment, {
            raised: true,
            style: {
                maxHeight: '80vh',
                overflow: 'auto'
            },
            children: content
        });
    } else {
        {
            return /*#__PURE__*/ _jsx("div", {
                className: clsx(className, compact && styles.compact, compact && styles.scrollable),
                children: /*#__PURE__*/ _jsx(NativeForm, {
                    children: boxes
                })
            });
        }
    }
};
export const FacetPopupMenuItem = ({ facets, mode, paramName, ...props })=>{
    let [showFacets, setShowFacets] = usePersistent('showFacets', false);
    return /*#__PURE__*/ _jsx(XMenu.PopupItem, {
        position: "bottom right",
        icon: "tasks",
        title: "Show Facets",
        ...props,
        children: /*#__PURE__*/ _jsx(Facets, {
            facets: facets,
            mode: mode,
            paramName: paramName,
            compact: true
        })
    });
};
